import { CARGA_PADRAO_ANCORAGEM_KGF } from './configuracaoAvisos'
import { fitaAbraca } from './fitaDeAncoragem'
import { posicaoVerticeAncoragem } from './geracaoAncoragens'
import { adicionarObjeto } from './operacoes'
import { adicionarAncoragem } from './ancoragens'
import type { AncoragemTabletop, CenaTabletop, CordaTabletop, ReferenciaPonto, TipoAncoragem } from './tipos'

/**
 * O V que se monta sozinho: duas fitas que abraçam pontos diferentes e
 * terminam na mesma peça (o mosquetão, o olhal da polia) SÃO uma ancoragem
 * em V; três, em W; quatro, em M. O instrutor não precisa desenhar a
 * ancoragem à parte: ela é reconhecida das fitas, e o ângulo e a carga por
 * perna aparecem do mesmo jeito.
 *
 * A ancoragem reconhecida não é gravada na cena: é recalculada das fitas a
 * cada vez, então acompanha qualquer mudança nelas. Uma ancoragem já
 * desenhada com o mesmo ponto central tem prioridade (não se duplica).
 *
 * TypeScript puro.
 */

/** Prefixo do id das ancoragens reconhecidas das fitas: não existem em `cena.ancoragens`. */
export const PREFIXO_ANCORAGEM_DE_FITAS = 'fitas:'

const TIPO_POR_PERNAS: Readonly<Record<number, TipoAncoragem>> = { 2: 'V', 3: 'W', 4: 'M' }

export interface AncoragemDeFitas extends AncoragemTabletop {
  /** As fitas que formam as pernas, na ordem das fixações. */
  readonly idsFitas: readonly string[]
}

const chave = (r: ReferenciaPonto) => `${r.idObjeto}.${r.idPonto}`

export function ancoragensDeFitas(cena: CenaTabletop): readonly AncoragemDeFitas[] {
  const objeto = (r: ReferenciaPonto) => cena.objetos.find((o) => o.id === r.idObjeto)
  // cada trecho de fita: a ponta que abraça a estrutura e a ponta que termina na peça
  const porCentro = new Map<string, { centro: ReferenciaPonto; pernas: Array<{ fixacao: ReferenciaPonto; idFita: string }> }>()
  for (const corda of cena.cordas ?? []) {
    if (corda.funcao !== 'fita') continue
    for (let i = 0; i + 1 < corda.percurso.length; i += 1) {
      const a = corda.percurso[i]!
      const b = corda.percurso[i + 1]!
      const oa = objeto(a)
      const ob = objeto(b)
      if (!oa || !ob) continue
      const abracaA = fitaAbraca(oa)
      const abracaB = fitaAbraca(ob)
      // só conta a fita que vai de uma estrutura a uma peça
      if (abracaA === abracaB) continue
      const [fixacao, centro] = abracaA ? [a, b] : [b, a]
      const grupo = porCentro.get(chave(centro)) ?? { centro, pernas: [] }
      if (!grupo.pernas.some((p) => chave(p.fixacao) === chave(fixacao))) grupo.pernas.push({ fixacao, idFita: corda.id })
      porCentro.set(chave(centro), grupo)
    }
  }
  const desenhadas = new Set((cena.ancoragens ?? []).map((a) => chave(a.centro)))
  const reconhecidas: AncoragemDeFitas[] = []
  for (const { centro, pernas } of porCentro.values()) {
    const tipo = TIPO_POR_PERNAS[pernas.length]
    if (!tipo || desenhadas.has(chave(centro))) continue
    reconhecidas.push({
      id: `${PREFIXO_ANCORAGEM_DE_FITAS}${chave(centro)}`,
      tipo,
      fixacoes: pernas.map((p) => p.fixacao),
      centro,
      cargaKgf: CARGA_PADRAO_ANCORAGEM_KGF,
      idsFitas: pernas.map((p) => p.idFita),
    })
  }
  return reconhecidas
}

export interface ResultadoJuncaoFitas {
  readonly cena: CenaTabletop
  readonly idPontoDiretor: string
  readonly idAncoragem: string
}

function encontrarFitaComPonta(cena: CenaTabletop, ref: ReferenciaPonto): { fita: CordaTabletop; outraPonta: ReferenciaPonto } | null {
  for (const corda of cena.cordas ?? []) {
    if (corda.funcao !== 'fita' || corda.percurso.length < 2) continue
    const primeiro = corda.percurso[0]!
    const ultimo = corda.percurso[corda.percurso.length - 1]!
    if (chave(primeiro) === chave(ref)) {
      return { fita: corda, outraPonta: ultimo }
    }
    if (chave(ultimo) === chave(ref)) {
      return { fita: corda, outraPonta: primeiro }
    }
  }
  return null
}

export function podeJuntarFitas(cena: CenaTabletop, refA: ReferenciaPonto, refB: ReferenciaPonto): boolean {
  if (chave(refA) === chave(refB)) return false
  const infoA = encontrarFitaComPonta(cena, refA)
  const infoB = encontrarFitaComPonta(cena, refB)
  if (!infoA || !infoB) return false
  if (infoA.fita.id === infoB.fita.id) return false
  return true
}

export function juntarFitasEmPonto(
  cena: CenaTabletop,
  refA: ReferenciaPonto,
  refB: ReferenciaPonto,
  gerarId: (prefixo?: string) => string,
): ResultadoJuncaoFitas {
  const infoA = encontrarFitaComPonta(cena, refA)
  const infoB = encontrarFitaComPonta(cena, refB)
  if (!infoA || !infoB || infoA.fita.id === infoB.fita.id) {
    throw new Error('As referências devem pertencer a duas fitas distintas.')
  }

  const fixacaoA = infoA.outraPonta
  const fixacaoB = infoB.outraPonta
  const posicao = posicaoVerticeAncoragem(cena, [fixacaoA, fixacaoB])
  const idPontoDiretor = gerarId('ponto')
  const idAncoragem = gerarId('ancoragem')
  const refCentro: ReferenciaPonto = { idObjeto: idPontoDiretor, idPonto: 'olhal' }

  // 1. Cria o novo ponto diretor
  let novaCena = adicionarObjeto(
    cena,
    {
      tipo: 'pontoAncoragem',
      nome: 'Vértice em V',
      transformacao: {
        posicao,
        rotacao: [0, 0, 0],
        escala: [1, 1, 1],
      },
      parametros: { alcaFita: 1 },
    },
    () => idPontoDiretor,
  )

  // 2. Atualiza os percursos das duas fitas para convergirem no ponto diretor
  const atualizarPercurso = (corda: CordaTabletop, antiga: ReferenciaPonto): CordaTabletop => ({
    ...corda,
    percurso: corda.percurso.map((p) => (chave(p) === chave(antiga) ? refCentro : p)),
  })

  novaCena = {
    ...novaCena,
    cordas: (novaCena.cordas ?? []).map((c) => {
      if (c.id === infoA.fita.id) return atualizarPercurso(c, refA)
      if (c.id === infoB.fita.id) return atualizarPercurso(c, refB)
      return c
    }),
  }

  // 3. Limpa objetos órfãos de alça de fita que não são mais usados
  const idsParaVerificar = new Set([refA.idObjeto, refB.idObjeto])
  idsParaVerificar.delete(idPontoDiretor)
  novaCena = {
    ...novaCena,
    objetos: novaCena.objetos.filter((obj) => {
      if (!idsParaVerificar.has(obj.id)) return true
      // Só remove se for ponto de ancoragem/alça sem outras cordas/ancoragens
      const aindaUsadoEmCordas = novaCena.cordas?.some((c) => c.percurso.some((p) => p.idObjeto === obj.id))
      const aindaUsadoEmAncoragens = novaCena.ancoragens?.some(
        (a) => a.centro.idObjeto === obj.id || a.fixacoes.some((f) => f.idObjeto === obj.id),
      )
      return aindaUsadoEmCordas || aindaUsadoEmAncoragens
    }),
  }

  // 4. Registra a ancoragem equalizada em V
  novaCena = adicionarAncoragem(
    novaCena,
    {
      tipo: 'V',
      fixacoes: [fixacaoA, fixacaoB],
      centro: refCentro,
      cargaKgf: CARGA_PADRAO_ANCORAGEM_KGF,
      rotulo: 'Ancoragem em V',
    },
    () => idAncoragem,
  )

  return {
    cena: novaCena,
    idPontoDiretor,
    idAncoragem,
  }
}

